import ImageGeneric from "@/assets/images/lg.png";
import { ModalBase } from "@/components/ModalBase/ModalBase";
import type { ChatCall } from "@/types/chatCall";
import { Loader2, Mic, MicOff, PhoneOff } from "lucide-react";

interface OngoingCallOverlayProps {
  open: boolean;
  call: ChatCall | null;
  isBusy?: boolean;
  isMuted?: boolean;
  durationSeconds?: number;
  connectionState?: "idle" | "connecting" | "connected" | "reconnecting";
  onCancel: () => void;
  onToggleMute: () => void;
  onEnd: () => void;
}

function formatDuration(durationSeconds = 0) {
  const totalSeconds = Math.max(0, Math.floor(durationSeconds));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
}

function getCallCopy(
  call: ChatCall | null,
  connectionState: OngoingCallOverlayProps["connectionState"],
) {
  if (!call) {
    return {
      badge: "Chamada de voz",
      title: "Preparando chamada",
      subtitle: "Aguarde um instante.",
    };
  }

  if (call.rawStatus === "calling") {
    return {
      badge: "Ligando",
      title: `${call.remoteParticipant.name} ${call.remoteParticipant.lastName}`.trim(),
      subtitle: "Aguardando atendimento do outro participante.",
    };
  }

  if (connectionState === "reconnecting") {
    return {
      badge: "Reconectando",
      title: `${call.remoteParticipant.name} ${call.remoteParticipant.lastName}`.trim(),
      subtitle: "Tentando restabelecer o áudio da chamada.",
    };
  }

  if (call.rawStatus === "answered" || connectionState === "connecting") {
    return {
      badge: "Conectando",
      title: `${call.remoteParticipant.name} ${call.remoteParticipant.lastName}`.trim(),
      subtitle: "A chamada foi atendida. Conectando ao áudio...",
    };
  }

  return {
    badge: "Chamada em andamento",
    title: `${call.remoteParticipant.name} ${call.remoteParticipant.lastName}`.trim(),
    subtitle: "Áudio conectado entre os participantes.",
  };
}

export function OngoingCallOverlay({
  open,
  call,
  isBusy = false,
  isMuted = false,
  durationSeconds = 0,
  connectionState = "idle",
  onCancel,
  onToggleMute,
  onEnd,
}: OngoingCallOverlayProps) {
  const copy = getCallCopy(call, connectionState);
  const canShowTimer = ["answered", "in_progress"].includes(call?.rawStatus || "");
  const canMute = ["answered", "in_progress"].includes(call?.rawStatus || "");
  const isCalling = call?.rawStatus === "calling";

  return (
    <ModalBase
      open={open}
      onOpenChange={() => undefined}
      title="Chamada de voz em andamento"
      closeOnOverlayClick={false}
      closeOnEsc={false}
      size="sm"
    >
      <div className="rounded-[30px] bg-white p-6 shadow-2xl">
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-[#153ECE]/10 animate-pulse" />
            <img
              src={call?.remoteParticipant.avatarUrl || ImageGeneric}
              alt={call?.remoteParticipant.name || "Usuário"}
              className="relative h-24 w-24 rounded-full object-cover"
            />
          </div>

          <div className="mt-5 space-y-1">
            <p className="text-sm font-medium text-azul-400">{copy.badge}</p>
            <h2 className="text-2xl font-semibold text-gray-900">{copy.title}</h2>
            <p className="text-sm text-gray-500">{copy.subtitle}</p>
            {canShowTimer && (
              <p className="mt-2 text-xl font-semibold tracking-[0.2em] text-gray-900">
                {formatDuration(durationSeconds)}
              </p>
            )}
            {!canShowTimer && connectionState === "connecting" && (
              <div className="mt-3 flex items-center justify-center gap-2 text-sm text-gray-500">
                <Loader2 size={16} className="animate-spin" />
                Conectando áudio
              </div>
            )}
          </div>

          <div className="mt-8 flex w-full gap-3">
            {isCalling ? (
              <button
                type="button"
                onClick={onCancel}
                disabled={isBusy}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-red-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <PhoneOff size={18} />
                Cancelar
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onToggleMute}
                  disabled={isBusy || !canMute}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    isMuted
                      ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
                  {isMuted ? "Ativar microfone" : "Silenciar"}
                </button>

                <button
                  type="button"
                  onClick={onEnd}
                  disabled={isBusy}
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-red-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <PhoneOff size={18} />
                  Encerrar
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </ModalBase>
  );
}
